import { Bot, UserCog, User, DollarSign, Users, Clock } from "lucide-react";
import type { UpworkAssignment, AssignmentStatus } from "../api/UpworkJobsApi";
import { STATUS_OPTIONS, TERMINAL } from "../api/UpworkJobsApi";
import { daysAgo, isStale, isVeryStale } from "./shared";
import StatusChangePopover from "shared/StatusChangePopover";
import StatusNotePopover from "shared/StatusNotePopover";
import ReassignPopover from "shared/ReassignPopover";

const STATUS_CHIP: Record<string, { label: string; className: string }> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, { label: o.label, className: TERMINAL.has(o.value) ? "bg-muted text-muted-foreground border-border" : "bg-primary text-primary-foreground border-primary/70" }])
);
// A few statuses get a distinct color to make scanning the board easier.
STATUS_CHIP.recommended = { label: "Recommended", className: "bg-violet-500 text-white border-violet-400" };
STATUS_CHIP.eligible = { label: "Eligible", className: "bg-teal-500 text-white border-teal-400" };
STATUS_CHIP.hired = { label: "Hired", className: "bg-emerald-500 text-emerald-950 border-emerald-400" };
STATUS_CHIP.rejected = { label: "Rejected", className: "bg-red-500 text-white border-red-400" };
STATUS_CHIP.applied_manually = { label: "Applied Manually", className: "bg-sky-500 text-sky-950 border-sky-400" };
STATUS_CHIP.interviewing = { label: "Interviewing", className: "bg-amber-500 text-amber-950 border-amber-400" };

interface Props {
  assignment: UpworkAssignment;
  showEmployee?: boolean;
  employeeName?: string;
  techManagers?: { id: number; name: string }[];
  onReassign?: (assignmentId: number, employeeId: number) => Promise<void>;
  onStatusChange?: (assignmentId: number, status: AssignmentStatus, notes: string | null) => Promise<void>;
  onClick: () => void;
  // Slimmer layout for the Kanban board, where columns are narrow and already
  // convey status by grouping — drops the status chip and the experience-level
  // / assigned-by badges in favor of fewer, tighter rows.
  compact?: boolean;
}

export default function AssignmentCard({ assignment, showEmployee, employeeName, techManagers, onReassign, onStatusChange, onClick, compact }: Props) {
  const statusEntry = STATUS_CHIP[assignment.status];

  if (compact) {
    return (
      <div
        onClick={onClick}
        className="bg-surface/60 border border-border rounded-xl p-3 cursor-pointer hover:border-muted-foreground transition-all select-none"
      >
        <h3 className="text-xs font-semibold text-foreground font-inter leading-snug line-clamp-2">{assignment.title}</h3>

        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-primary/10 text-primary border-primary/20 font-inter">
            {assignment.search_profile}
          </span>
          {assignment.amount_display_value && (
            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/20 font-inter">
              <DollarSign className="w-2.5 h-2.5" />
              {assignment.amount_display_value}
            </span>
          )}
          {isStale(assignment.discovered_at) && (
            <span
              title={`Cached ${daysAgo(assignment.discovered_at).toLowerCase()} — details may be outdated`}
              className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border font-inter cursor-help ${
                isVeryStale(assignment.discovered_at)
                  ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                  : "bg-muted/40 text-muted-foreground border-border"
              }`}
            >
              <Clock className="w-2.5 h-2.5" />
              {isVeryStale(assignment.discovered_at) ? "Stale" : "May be outdated"}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 mt-2">
          {assignment.total_applicants != null && (
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-inter">
              <Users className="w-3 h-3" />
              {assignment.total_applicants}
            </span>
          )}
          {showEmployee && employeeName && (
            <div className="ml-auto">
              {onReassign ? (
                <ReassignPopover
                  candidates={techManagers ?? []}
                  currentEmployeeId={assignment.employee_id ?? null}
                  onReassign={(employeeId) => onReassign(assignment.assignment_id, employeeId)}
                >
                  <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter truncate max-w-[120px]">
                    <User className="w-2.5 h-2.5 shrink-0" />
                    {employeeName}
                  </span>
                </ReassignPopover>
              ) : (
                <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter truncate max-w-[120px]">
                  <User className="w-2.5 h-2.5 shrink-0" />
                  {employeeName}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="bg-surface/60 border border-border rounded-xl p-4 cursor-pointer hover:border-muted-foreground transition-all select-none"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground font-inter leading-snug line-clamp-2 flex-1">{assignment.title}</h3>
        {statusEntry && (
          onStatusChange ? (
            <StatusChangePopover
              options={STATUS_OPTIONS}
              statusChipMap={STATUS_CHIP}
              currentStatus={assignment.status}
              note={assignment.assignment_notes}
              fromLabel={assignment.assignment_from_status ? STATUS_CHIP[assignment.assignment_from_status]?.label : null}
              disabled={TERMINAL.has(assignment.status)}
              disabledMessage="This assignment is in a terminal state and cannot be updated."
              onChange={(status, notes) => onStatusChange(assignment.assignment_id, status as AssignmentStatus, notes)}
            >
              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className}`}>
                {statusEntry.label}
              </span>
            </StatusChangePopover>
          ) : (
            <StatusNotePopover
              note={assignment.assignment_notes}
              fromLabel={assignment.assignment_from_status ? STATUS_CHIP[assignment.assignment_from_status]?.label : null}
              toLabel={statusEntry.label}
            >
              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className} ${assignment.assignment_notes ? "cursor-help" : ""}`}>
                {statusEntry.label}
              </span>
            </StatusNotePopover>
          )
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-primary/10 text-primary border-primary/20 font-inter">
          {assignment.search_profile}
        </span>
        {assignment.experience_level && (
          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter">
            {assignment.experience_level}
          </span>
        )}
        {assignment.amount_display_value && (
          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/20 font-inter">
            <DollarSign className="w-2.5 h-2.5" />
            {assignment.amount_display_value}
          </span>
        )}
        <span
          className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border font-inter ${
            assignment.assigned_by === "auto"
              ? "bg-violet-500/10 text-violet-400 border-violet-500/20"
              : "bg-muted/50 text-muted-foreground border-border"
          }`}
        >
          {assignment.assigned_by === "auto" ? <Bot className="w-2.5 h-2.5" /> : <UserCog className="w-2.5 h-2.5" />}
          {assignment.assigned_by === "auto" ? "Auto" : "Manual"}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2">
        {assignment.total_applicants != null && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
            <Users className="w-3 h-3" />
            {assignment.total_applicants} applicants
          </span>
        )}
        {isStale(assignment.discovered_at) && (
          <span
            title={`Cached ${daysAgo(assignment.discovered_at).toLowerCase()} — details may be outdated`}
            className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border font-inter cursor-help ${
              isVeryStale(assignment.discovered_at)
                ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                : "bg-muted/40 text-muted-foreground border-border"
            }`}
          >
            <Clock className="w-2.5 h-2.5" />
            {isVeryStale(assignment.discovered_at) ? "Stale" : "May be outdated"}
          </span>
        )}
        {showEmployee && employeeName && (
          <div className="ml-auto">
            {onReassign ? (
              <ReassignPopover
                candidates={techManagers ?? []}
                currentEmployeeId={assignment.employee_id ?? null}
                onReassign={(employeeId) => onReassign(assignment.assignment_id, employeeId)}
              >
                <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter truncate max-w-[120px]">
                  <User className="w-2.5 h-2.5 shrink-0" />
                  {employeeName}
                </span>
              </ReassignPopover>
            ) : (
              <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border font-inter truncate max-w-[120px]">
                <User className="w-2.5 h-2.5 shrink-0" />
                {employeeName}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
